import { CheckCircleIcon } from "@heroicons/react/24/outline";
import { CircleIcon } from "@Light/components/page/CircleIcon";
import { useScaffold } from "@Light/scaffold";
import { formatBaseFee } from "@Light/utils/evProgram";
import { useOptionalPlan, useOptionalRate } from "@Light/utils/plan";
import { planFeaturesForRateGroup } from "@Light/utils/planFeatures";
import { FixedRateIcon } from "./ConfirmRate/FixedRateIcon";
import { PreactivationCreditIcon } from "./ConfirmRate/PreactivationCreditIcon";
import { RenewableIcon } from "./ConfirmRate/RenewableIcon";
import { SolarBuybackIcon } from "./ConfirmRate/SolarBuybackIcon";
import { VehicleChargingIcon } from "./ConfirmRate/VehicleChargingIcon";

export type Highlight = {
  key: string;
  icon: React.ReactNode;
  text: React.ReactNode;
};

const Item = ({ icon, text }: Omit<Highlight, "key">) => (
  <div className="flex items-center gap-2">
    {icon}
    <div>{text}</div>
  </div>
);

export const usePlanHighlights = (): Highlight[] => {
  const scaffold = useScaffold();
  const rate = useOptionalRate();
  const optionalPlan = useOptionalPlan();

  // Prefer the address-specific rate when available, otherwise use the plan.
  const rateOrPlan = rate || optionalPlan?.plan || optionalPlan?.group;
  if (!rateOrPlan) {
    return [];
  }

  const {
    solarBuyback,
    storageCredit,
    vehicleCharging,
    fixedRate,
    renewableEnergy,
    preactivationCredit,
  } = planFeaturesForRateGroup(rateOrPlan);

  const isPalmetto = scaffold.page.companyName === "Palmetto";
  const isJustSolarBuyback = Boolean(solarBuyback && !storageCredit);
  const isSolarBuybackWithStorage = Boolean(solarBuyback && storageCredit);
  const showRenewableEnergy = renewableEnergy && !preactivationCredit;

  const highlights: Highlight[] = [];

  if (isPalmetto) {
    highlights.push({
      key: "all-in-one",
      icon: (
        <CircleIcon className="bg-slate-50">
          <CheckCircleIcon className="w-4 text-slate-500" />
        </CircleIcon>
      ),
      text: "All your power in one place",
    });
  }
  if (isJustSolarBuyback) {
    highlights.push({
      key: "solar-buyback",
      icon: <SolarBuybackIcon />,
      text: "Unlimited credit for solar buyback",
    });
  }
  if (isSolarBuybackWithStorage) {
    highlights.push({
      key: "solar-buyback-storage",
      icon: <SolarBuybackIcon />,
      text: "Rewards for solar buyback & battery use",
    });
  }
  if (!isPalmetto && fixedRate) {
    highlights.push({
      key: "fixed-rate",
      icon: <FixedRateIcon />,
      text: `A locked-in rate for the next ${fixedRate.termMonths} months`,
    });
  }
  if (showRenewableEnergy) {
    highlights.push({
      key: "renewable",
      icon: <RenewableIcon />,
      text: "100% renewable home energy",
    });
  }
  if (preactivationCredit) {
    highlights.push({
      key: "preactivation-credit",
      icon: <PreactivationCreditIcon className="shrink-0" />,
      text: `${preactivationCredit.percent}% off your grid energy charge until your system is active`,
    });
  }
  if (vehicleCharging) {
    highlights.push({
      key: "vehicle-charging",
      icon: <VehicleChargingIcon />,
      text: `Unlimited vehicle charging for ${formatBaseFee(
        vehicleCharging.baseFee,
      )}/month`,
    });
  }

  return highlights;
};

/** The bare list of highlight rows, with no container or label. */
export const PlanHighlights = () => {
  const highlights = usePlanHighlights();
  return (
    <>
      {highlights.map(({ key, icon, text }) => (
        <Item key={key} icon={icon} text={text} />
      ))}
    </>
  );
};
